import { Cell as CellType } from '@convergence/types';
import CodeMirror from '@uiw/react-codemirror';
import { Identifier } from 'dnd-core';
import { forwardRef, RefObject, useEffect, useState } from 'react';
import { NotebookStore } from '../stores/NotebookStore';
import { CellOptions } from './Cell';
import { langs } from '@uiw/codemirror-extensions-langs';
import ReactMarkdown from 'react-markdown';
import MarkdownPreview from '@uiw/react-markdown-preview';

interface MarkdownCellProps {
  cell: CellType;
  index: number;
  readonly: boolean;
  store: NotebookStore;
  activeCellId: string | null;
  ref: RefObject<HTMLDivElement>;
  isDragging: boolean;
  handlerId: Identifier | null;
}

export const MarkdownCell = forwardRef<HTMLDivElement, MarkdownCellProps>(
  (
    { cell, index, readonly, store, activeCellId, isDragging, handlerId },
    ref
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const cellSourceStr =
      typeof cell.source === 'string' ? cell.source : cell.source.join('\n');
    const markdown = cellSourceStr.replace(/\\n/g, '\n');

    const moveProps = readonly
      ? {}
      : { ref: ref, 'data-handler-id': handlerId };

    useEffect(() => {
      if (activeCellId !== cell.id) {
        setIsEditing(false);
      }
    }, [activeCellId]);

    return (
      <div style={{ opacity: isDragging ? 0.5 : 1 }} className="relative">
        <div
          className="py-2 flex gap-1"
          onClick={() => store.activateCell(cell.id)}
        >
          <div
            className={`w-3 cursor-move ${
              activeCellId === cell.id ? 'bg-orange-2' : 'bg-none'
            }`}
            {...moveProps}
          ></div>
          {isEditing ? (
            <CodeMirror
              className="w-full overflow-x-auto"
              value={cellSourceStr}
              extensions={[langs.markdown()]}
              editable={!readonly}
              onChange={(code) => {
                store.updateCell(code, cell.id);
              }}
            />
          ) : (
            <div
              className="p-4 bg-white rounded-sm border border-black w-full overflow-x-auto font-terminal prose max-w-none"
              onClick={() => {
                store.activateCell(cell.id);
              }}
              onDoubleClick={() => {
                store.activateCell(cell.id);
                setIsEditing(true);
              }}
            >
              <MarkdownPreview
                source={markdown}
                className="markdown-styles font-body"
              />
            </div>
          )}
          {activeCellId === cell.id && !readonly && (
            <CellOptions
              cellIndex={index}
              cellId={cell.id}
              cellType={cell.cell_type}
              deleteCell={store.deleteCell}
              moveCell={store.moveCell}
              changeCellType={store.changeCellType}
            />
          )}
        </div>
      </div>
    );
  }
);
